import { CaptureContext } from "@zumer/snapdom";

/**
 * Snapdom plugin that preserves canvas content during DOM cloning.
 *
 * Problem:
 *   snapdom clones the DOM tree before serializing it to an image, but cloned
 *   <canvas> elements lose their pixel data — they are blank by default.
 *   On macOS this is often masked because the browser retains the backing store
 *   longer, but on Windows with Chrome's ANGLE/DirectX compositing the GPU
 *   buffer is aggressively flushed, resulting in blank chart widgets in
 *   dashboard screenshots.
 *
 * Solution:
 *   After the clone is created, we iterate every <canvas> in the *live* DOM,
 *   call toDataURL() to capture the current pixels, and replace the
 *   corresponding cloned <canvas> with a static <img>. This is the same
 *   approach already used for Mapbox WebGL canvases (preserveDrawingBuffer
 *   issue), but generalized to cover ECharts and any other canvas-based
 *   widgets on the dashboard.
 *
 * Scope:
 *   The plugin targets all canvases under the captured element. It is safe to
 *   use alongside the Mapbox-specific plugin — Mapbox canvases will simply be
 *   handled by whichever plugin runs first.
 */

/** Convert a live canvas to a static <img> preserving dimensions and style. */
const canvasToImage = (canvas: HTMLCanvasElement): HTMLImageElement | null => {
  try {
    const img = document.createElement("img");
    img.src = canvas.toDataURL("image/png");
    img.width = canvas.width;
    img.height = canvas.height;
    img.style.cssText = canvas.style.cssText;
    return img;
  } catch {
    // Tainted canvas (e.g. cross-origin content) — skip gracefully
    return null;
  }
};

/** Replace each cloned canvas with a static snapshot read from the live DOM. */
const replaceClonedCanvases = (clone: HTMLElement): void => {
  const liveCanvases = Array.from(document.querySelectorAll<HTMLCanvasElement>("canvas"));
  const clonedCanvases = Array.from(clone.querySelectorAll<HTMLCanvasElement>("canvas"));

  liveCanvases.forEach((liveCanvas, index) => {
    const clonedCanvas = clonedCanvases[index];
    if (!clonedCanvas) return;

    const img = canvasToImage(liveCanvas);
    if (img) clonedCanvas.replaceWith(img);
  });
};

const canvasPlugin = {
  name: "canvas-snapshot",
  async afterClone(context: CaptureContext) {
    if (context.clone instanceof HTMLElement) {
      replaceClonedCanvases(context.clone);
    }
  }
};

export default canvasPlugin;
